import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'flowbite-react';
import { XIcon, SvgDownload } from '../../../components/icons';
import { useTranscript } from '../../../hooks/useTranscript';
import { logger, LogCategory } from '../../../services/logger';
import { TranscriptViewerProps, TranscriptSegment } from '../../../types/transcript';
import TranscriptContent from './TranscriptContent';
import TranscriptSearch from './TranscriptSearch';
import TranscriptExport from './TranscriptExport';

export default function TranscriptViewer({ 
    recordingId, 
    recordingTitle,
    isOpen, 
    onClose,
    videoTimestamp,
    onTimestampClick 
}: TranscriptViewerProps) {
    const {
        metadata,
        transcriptContent,
        isLoading,
        hasTranscript,
        searchQuery,
        setSearchQuery,
        searchResults,
        exportTranscript,
        error
    } = useTranscript(recordingId, isOpen, recordingTitle);
    
    const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            logger.info(
                LogCategory.TRANSCRIPT_USER_ACTION,
                'TRANSCRIPT_MODAL_OPENED',
                'User opened transcript viewer',
                {
                    recordingId,
                    recordingTitle,
                    hasVideoTimestamp: !!videoTimestamp
                }
            );
            
            const openTime = Date.now();
            
            return () => {
                const viewDuration = Date.now() - openTime;
                logger.metric(
                    LogCategory.TRANSCRIPT_USER_ACTION,
                    'TRANSCRIPT_VIEW_DURATION',
                    { duration: viewDuration },
                    { recordingId, recordingTitle }
                );
            };
        }
    }, [isOpen, recordingId, recordingTitle, videoTimestamp]);

    useEffect(() => {
        if (videoTimestamp && transcriptContent) {
            const activeSegment = transcriptContent.segments.find(
                segment => videoTimestamp >= segment.startTime && videoTimestamp <= segment.endTime
            );
            if (activeSegment) {
                setActiveSegmentId(activeSegment.id);
                scrollToSegment(activeSegment.id);
            }
        }
    }, [videoTimestamp, transcriptContent]);

    const scrollToSegment = (segmentId: string) => {
        const element = document.getElementById(`segment-${segmentId}`);
        if (element && contentRef.current) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleSegmentClick = (segment: TranscriptSegment) => {
        logger.info(
            LogCategory.TRANSCRIPT_USER_ACTION,
            'TRANSCRIPT_SEGMENT_CLICK',
            'User clicked on transcript segment',
            {
                recordingId,
                segmentId: segment.id,
                timestamp: segment.startTime,
                segmentText: segment.text.substring(0, 50) + '...'
            }
        );
        
        if (onTimestampClick) {
            onTimestampClick(segment.startTime);
            
            logger.info(
                LogCategory.TRANSCRIPT_USER_ACTION,
                'VIDEO_TIMESTAMP_NAVIGATION',
                `Navigating video to ${segment.startTime}s`,
                {
                    recordingId,
                    targetTimestamp: segment.startTime,
                    fromTimestamp: videoTimestamp
                }
            );
        }
        
        setActiveSegmentId(segment.id);
    };

    const handleExport = (format: string) => {
        const exportStartTime = performance.now();
        
        logger.info(
            LogCategory.TRANSCRIPT_USER_ACTION,
            'EXPORT_INITIATED',
            `User initiated ${format} export`,
            {
                recordingId,
                format,
                transcriptLength: transcriptContent?.fullText.length,
                segmentCount: transcriptContent?.segments.length
            }
        );
        
        try {
            exportTranscript(format);
            const exportDuration = performance.now() - exportStartTime;
            
            logger.info(
                LogCategory.TRANSCRIPT_USER_ACTION,
                'EXPORT_COMPLETED',
                `${format} export completed in ${exportDuration}ms`,
                {
                    recordingId,
                    format,
                    duration: exportDuration
                }
            );
        } catch (error) {
            logger.error(
                LogCategory.TRANSCRIPT_ERROR,
                'EXPORT_FAILED',
                `Failed to export transcript as ${format}`,
                error as Error,
                { recordingId, format }
            );
        }
    };

    const handleSearchChange = (query: string) => {
        logger.info(
            LogCategory.TRANSCRIPT_USER_ACTION,
            'SEARCH_QUERY_CHANGED',
            `Search query: "${query}"`,
            {
                recordingId,
                query,
                queryLength: query.length,
                previousQuery: searchQuery
            }
        );
        
        setSearchQuery(query);
    };

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.ctrlKey || event.metaKey) {
                const key = event.key.toLowerCase();
                
                if (key === 'f') {
                    event.preventDefault();
                    logger.info(
                        LogCategory.TRANSCRIPT_USER_ACTION,
                        'KEYBOARD_SHORTCUT',
                        'User triggered search with Ctrl+F',
                        { recordingId }
                    );
                } else if (key === 's') {
                    event.preventDefault();
                    logger.info(
                        LogCategory.TRANSCRIPT_USER_ACTION,
                        'KEYBOARD_SHORTCUT',
                        'User triggered export with Ctrl+S',
                        { recordingId }
                    );
                }
            }
        };
        
        if (isOpen) {
            document.addEventListener('keydown', handleKeyPress);
            return () => document.removeEventListener('keydown', handleKeyPress);
        }
    }, [isOpen, recordingId]);

    const formatTimestamp = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    return (
        <Modal show={isOpen} onClose={onClose} size="7xl" className="relative z-50">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        Meeting Transcript
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">{recordingTitle}</p>
                </div>
                <button
                    onClick={onClose}
                    className="rounded-md p-2 hover:bg-gray-100 transition-colors"
                >
                    <XIcon className="h-5 w-5" />
                </button>
            </div>
                    
            <Modal.Body className="p-0">
                {isLoading ? (
                    <div className="flex items-center justify-center h-96">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-400"></div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                        <p className="text-red-600 mb-2">Error loading transcript</p>
                        <p className="text-sm">{error.message}</p>
                    </div>
                ) : !hasTranscript ? (
                    <div className="flex flex-col items-center justify-center h-96 text-gray-500">
                        <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg mb-2">No transcript available</p>
                        <p className="text-sm">This recording does not have a transcript yet.</p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-4 p-4 border-b border-gray-200">
                            <TranscriptSearch 
                                searchQuery={searchQuery}
                                onSearchChange={handleSearchChange}
                                resultCount={searchResults.length}
                                placeholder="Search transcript..."
                            />
                            <div className="flex items-center gap-2">
                                {/* Quick Download TXT Button */}
                                <button
                                    onClick={() => handleExport('TXT')}
                                    disabled={!transcriptContent}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                                    title="Download as Text File"
                                >
                                    <SvgDownload className="h-4 w-4" />
                                    Download TXT
                                </button>
                                {/* Other Export Options */}
                                <TranscriptExport 
                                    onExport={handleExport}
                                    formats={['JSON', 'SRT', 'PDF']}
                                    disabled={!transcriptContent}
                                />
                            </div>
                        </div>
                        
                        <div className="h-96 overflow-y-auto p-6" ref={contentRef}>
                            <TranscriptContent
                                segments={searchResults.length > 0 ? searchResults : transcriptContent?.segments || []}
                                activeSegmentId={activeSegmentId}
                                onSegmentClick={handleSegmentClick}
                                highlightQuery={searchQuery}
                            />
                        </div>
                        
                        {hasTranscript && metadata && (
                            <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                                <div className="text-sm text-gray-500">
                                    Format: {metadata.transcriptFormat.toUpperCase()} • 
                                    Uploaded: {new Date(metadata.transcriptUploadedAt).toLocaleDateString()}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {transcriptContent?.segments.length || 0} segments • 
                                    {transcriptContent?.speakers.length || 0} speakers
                                    {transcriptContent?.duration && transcriptContent.duration > 0 && (
                                        <> • Duration: {formatTimestamp(transcriptContent.duration)}</>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}